import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../../api/client";
import type { LineReanchor, Phase, PhaseLine } from "../../api/types";
import { unitDesignation } from "../../i18n/designation";
import { k, locale, t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import {
  doctrinalPlacement,
  dossierWrite,
  placementOf,
  reanchoredPlacement,
  recordWrite,
  undoAction,
  useDossierHistory,
  writeFailure,
} from "../dossierWrites";
import { useHistoryState } from "../history";
import { useLineLabel } from "../lineNames";
import { measureLevelOf } from "../manoeuvre/measureView";
import { orderActions, withOrders } from "../orderSync";
import { defensiveRoles } from "./propagation";
import { showPropagation } from "./showPropagation";
import { useDossierLocked } from "../../sharing/access";

/** Déplacements manuels des unités et glissements des lignes de coordination : enregistrement, avis
 *  (10 s, comme l'aperçu de l'iPad), annulation par l'historique de la carte, retour aux positions doctrinales. */

/** Rôles des unités dans le dispositif : clés de traduction, affichées par `t(ROLE_LABEL[rôle])`. */
export const ROLE_LABEL: Record<string, string> = {
  recoAmi: k("reconnaissance"),
  ech1Ami: k("1er échelon"),
  ech2Ami: k("2e échelon"),
  renfortsAmi: k("renforts"),
  edc: k("EDC — élément décisif coordonné"),
  eei: k("EEI — élément d'effort initial"),
};

/** Rôles d'une défense (registre A126) : ni reconnaissance, 1er et 2e échelons défensifs, renforts. */
const DEFENSIVE_ROLE_LABEL: Record<string, string> = {
  recoAmi: k("1er échelon défensif"),
  ech1Ami: k("1er échelon défensif"),
  ech2Ami: k("2e échelon défensif"),
};

/** Libellé affiché d'un rôle, dans la langue de l'interface (`undefined` : rôle inconnu) ; `defensive` : rôles d'une
 *  défense (registre A126). */
export function roleName(role: string | null | undefined, defensive = false): string | undefined {
  const label = (defensive ? DEFENSIVE_ROLE_LABEL[role ?? ""] : undefined) ?? ROLE_LABEL[role ?? ""];
  return label === undefined ? undefined : t(label);
}

type LineName = PhaseLine["name"];

/** État des déplacements d'un déploiement, normalisé (voir `map/dossierWrites.ts`). */
export { doctrinalPlacement, placementOf } from "../dossierWrites";

function signedHours(hours: number): string {
  return `${hours > 0 ? "+" : hours < 0 ? "−" : "±"}${Math.abs(hours)} h`;
}

interface MoveInput {
  unit: string;
  phase: Phase;
  lon: number;
  lat: number;
}

/** Déplacements, lignes, retour au dispositif doctrinal et potentiel. Toutes les écritures passent par la portée
 *  du dossier (`dossierWrite`) et s'inscrivent dans l'historique de la carte : canal « dispositif » pour les
 *  unités, les LIMA et le retour au dispositif doctrinal, canal « potentiel » pour le potentiel ; l'action
 *  « Annuler » des avis défait par l'historique. */
export function useUnitMoves() {
  const queryClient = useQueryClient();
  const locked = useDossierLocked();
  const params = useUi((s) => s.deploymentParams);
  const notify = useUi((s) => s.notify);
  const lineLabel = useLineLabel();
  const setDragPreview = useUi((s) => s.setDragPreview);
  const setLinePreview = useUi((s) => s.setLinePreview);
  const isCentac = params?.level === "CENTAC";
  const historyBusy = useHistoryState((s) => s.busy);
  useDossierHistory();

  const failure = (error: unknown) => writeFailure(queryClient, error, t("Déplacement non enregistré"));

  const move = useMutation({
    mutationFn: (input: MoveInput) =>
      dossierWrite(queryClient, (target, opo) => api.moveUnit(target, { ...input, opo }), {
        notSaved: t("Enregistrez le dossier pour déplacer les unités."),
        channel: "dispositif",
      }),
    onSuccess: (write, input) => {
      const { result } = write;
      const unit = result.units.find((u) => u.key === input.unit);
      const locality = unit?.positions[input.phase]?.locality;
      const designation = unit ? unitDesignation(unit.designation) : t("Unité");
      const action = undoAction(recordWrite(write, t("déplacement de {unit}", { unit: designation })));
      const place = locality ? ` — ${locality}` : "";
      if (isCentac) {
        const friend = unit?.side === "AMI";
        notify({
          text:
            (friend
              ? t("{unit} déplacée pour l'OPO {opo}", { unit: designation, opo: result.centac?.opo ?? 1 })
              : t("{unit} déplacée en {phase}", { unit: designation, phase: input.phase })) + place,
          detail: withOrders(
            friend
              ? t("Position valable de T1 à T3 de cet OPO ; fuseau et LIMA recalculés, missions de la rotation inchangées.")
              : t("Position valable pour ce temps dans tous les OPO ; fuseau et LIMA recalculés."),
            result,
          ),
          tone: "ok",
          ...orderActions(action, result),
        });
        return;
      }
      // Rôles relus et missions qui suivent (docs/25 §6) : l'avis récapitulatif, cause → effets.
      if (showPropagation(write, action !== undefined, place ? locality ?? undefined : undefined)) {
        notify(null);
        return;
      }
      const changes = result.moves.role_changes.map((c) =>
        t("{unit} : {before} → {after}", {
          unit: unitDesignation(c.designation),
          before: roleName(c.before, defensiveRoles(result)) ?? "—",
          after: roleName(c.after, defensiveRoles(result)) ?? c.after,
        }),
      );
      // Avis du serveur (ennemi posé derrière la ligne amie…) : l'avis passe en avertissement.
      const warned = Boolean(result.notices?.length);
      notify({
        text: t("{unit} déplacée en {phase}", { unit: designation, phase: input.phase }) + place,
        detail: withOrders(
          changes.length
            ? t("Rôles recalculés en {phase} (écarts au dispositif doctrinal), les missions suivent : {changes}", {
                phase: result.moves.roles_phase,
                changes: changes.join(" · "),
              })
            : t("Rôles conformes au dispositif doctrinal : les missions restent celles par défaut."),
          result,
        ),
        tone: warned ? "warn" : "ok",
        ...orderActions(action, result),
      });
    },
    onError: failure,
    onSettled: () => setDragPreview(null),
  });

  // Retour à la position doctrinale d'une unité (à un temps ou à tous) ou de toutes (`DELETE /positions`) : les
  // missions choisies sont conservées.
  const reset = useMutation({
    mutationFn: (scope: { unit?: string; phase?: Phase }) =>
      dossierWrite(queryClient, (target, opo) => api.resetPositions(target, scope, opo), {
        notSaved: t("Dossier non enregistré"),
        channel: "dispositif",
      }),
    onSuccess: (write, scope) => {
      const { result } = write;
      const unit = scope.unit ? result.units.find((u) => u.key === scope.unit) : undefined;
      const recorded = recordWrite(
        write,
        scope.unit
          ? t("position doctrinale de {unit}", { unit: unit ? unitDesignation(unit.designation) : t("Unité") })
          : t("retour aux positions doctrinales"),
      );
      notify({
        text: scope.unit
          ? t("Position doctrinale rétablie")
          : isCentac
            ? t("Dispositif doctrinal rétabli")
            : t("Positions doctrinales rétablies ; missions choisies conservées"),
        detail: withOrders(isCentac ? t("Fuseau et LIMA recalculés.") : t("Rôles et missions recalculés."), result),
        tone: "info",
        ...orderActions(undoAction(recorded), result),
      });
    },
    onError: failure,
  });

  // Glissement d'une ligne : GDH décalé de 10 h pour toute la longueur du fuseau (fuseau personnalisé : la LIMA
  // glisse dans son repère, inclinaison et courbures gardées).
  const moveLine = useMutation({
    mutationFn: (input: { line: LineName; lon: number; lat: number }) =>
      dossierWrite(queryClient, (target, opo) => api.moveLine(target, input, opo), {
        notSaved: t("Enregistrez le dossier pour déplacer les lignes."),
        channel: "dispositif",
      }),
    onSuccess: (write, input) => {
      const { result, previous: before } = write;
      const recorded = recordWrite(write, t("glissement de {line}", { line: lineLabel(input.line) }));
      const previous = before.corridor?.lines.find((l) => l.name === input.line);
      const line = result.corridor?.lines.find((l) => l.name === input.line);
      const km = ((line?.shift_m ?? 0) / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 });
      const unchanged = previous?.offset_h === line?.offset_h;
      const plan = result.reanchor;
      if (plan) {
        // LIMA glissée au-delà de positions amies : glissement gardé, l'avis dit lesquelles (texte du serveur dans
        // le détail) et désigne l'action attendue, les réancrer.
        notify({
          text: t("{line} glissée au-delà de positions amies", { line: lineLabel(input.line) }),
          detail: withOrders(undefined, result),
          tone: "warn",
          action: { label: t("Réancrer les positions"), run: () => reanchor.mutate(plan), attention: true },
          secondary: undoAction(recorded),
        });
        return;
      }
      notify({
        text: unchanged
          ? line?.dtg
            ? t("{line} déplacée — GDH inchangé ({dtg})", { line: input.line, dtg: line.dtg })
            : t("{line} déplacée — GDH inchangé", { line: input.line })
          : t("{line} déplacée : {change}", {
              line: input.line,
              change: previous?.dtg && line?.dtg ? `${previous.dtg} → ${line.dtg}` : signedHours(line?.offset_h ?? 0),
            }),
        detail: withOrders(
          unchanged
            ? t("Glissement de {km} km le long du fuseau, trop court pour décaler le GDH d'une heure (10 h pour toute la longueur du fuseau).", { km })
            : t("Glissement de {km} km le long du fuseau ; GDH décalé de {hours} (10 h pour toute la longueur du fuseau).", {
                km,
                hours: signedHours(line?.offset_h ?? 0),
              }),
          result,
        ),
        tone: "ok",
        ...orderActions(undoAction(recorded), result),
      });
    },
    onError: failure,
    onSettled: () => setLinePreview(null),
  });

  // Réancrage des positions dépassées par une LIMA glissée (`Deployment.reanchor`) : positions proposées par le
  // serveur, écrites comme des déplacements à la main (une entrée d'historique).
  const reanchor = useMutation({
    mutationFn: (plan: LineReanchor) =>
      dossierWrite(
        queryClient,
        (target, opo, current) => api.setPlacement(target, reanchoredPlacement(current, plan), opo),
        { notSaved: t("Dossier non enregistré"), channel: "dispositif" },
      ),
    onSuccess: (write, plan) => {
      const recorded = recordWrite(write, t("réancrage des positions sur {line}", { line: lineLabel(plan.line) }));
      notify({
        text: t("Positions réancrées sur {line}", { line: lineLabel(plan.line) }),
        detail: withOrders(undefined, write.result),
        tone: "ok",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: failure,
  });

  const resetLine = useMutation({
    mutationFn: (line?: LineName) =>
      dossierWrite(queryClient, (target, opo) => api.resetLines(target, line, opo), {
        notSaved: t("Dossier non enregistré"),
        channel: "dispositif",
      }),
    onSuccess: (write, line) => {
      const recorded = recordWrite(
        write,
        line
          ? t("retour de {line} à sa position doctrinale", { line: lineLabel(line) })
          : t("retour des lignes à leur position doctrinale"),
      );
      notify({
        text: line ? t("{line} rétablie à sa position doctrinale", { line: lineLabel(line) }) : t("Lignes rétablies"),
        detail: withOrders(undefined, write.result),
        tone: "info",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: failure,
  });

  // « Revenir au dispositif doctrinal » (docs/14 §2.2) : unités (tous les OPO en CENTAC), missions et ancrages
  // (null), LIMA ramenées à leur position doctrinale sans GDH saisi ni courbure ; géométrie du fuseau gardée.
  const resetAll = useMutation({
    mutationFn: () =>
      dossierWrite(
        queryClient,
        (target, opo, current) => api.setPlacement(target, doctrinalPlacement(current), opo),
        { notSaved: t("Dossier non enregistré"), channel: "dispositif" },
      ),
    onSuccess: (write) => {
      const recorded = recordWrite(write, t("retour au dispositif doctrinal"));
      const kept = Boolean(placementOf(write.previous).corridor); // fuseau effectivement lu (voir `placementOf`)
      // Lignes de la ZO rétablies aussi (`measures: null`, docs/15 §3.8) : annoncé seulement si le dispositif en avait
      // (ni en CENTAC, ni sans fuseau ou sans lignes calculées).
      const zo = measureLevelOf(write.previous) !== null;
      const text = zo
        ? kept
          ? t("Unités, missions, LIMA et lignes de la ZO rétablies ; fuseau conservé.")
          : t("Unités, missions, LIMA et lignes de la ZO rétablies.")
        : kept
          ? t("Unités, missions et LIMA rétablies ; fuseau conservé.")
          : t("Unités, missions et LIMA rétablies.");
      notify({
        text,
        detail: withOrders(undefined, write.result),
        tone: "info",
        ...orderActions(undoAction(recorded), write.result),
      });
    },
    onError: failure,
  });

  // Potentiel d'une unité (curseur POTENTIEL de l'iPad) : quantité engagée, affichée au survol ; sans effet sur
  // l'ordre. Canal « potentiel » de l'historique (sans avis : le curseur suffit).
  const setStrength = useMutation({
    mutationFn: (input: { unit: string; value: number | null }) =>
      dossierWrite(queryClient, (target, opo) => api.setStrength(target, input.unit, input.value, opo), {
        notSaved: t("Enregistrez le dossier pour régler le potentiel."),
        channel: "potentiel",
        orders: false,
      }),
    onSuccess: (write, input) => {
      const unit = write.result.units.find((u) => u.key === input.unit);
      recordWrite(write, t("potentiel de {unit}", { unit: unit ? unitDesignation(unit.designation) : t("Unité") }));
    },
    onError: (error) => writeFailure(queryClient, error, t("Potentiel non enregistré")),
  });

  return {
    // Dossier en lecture seule pour le compte (docs/17 §3.2) : rien ne se déplace, l'appui désigne le bandeau.
    canMove: Boolean(params?.exercise_id) && !locked,
    setStrength,
    move,
    reset,
    moveLine,
    reanchor,
    resetLine,
    resetAll,
    /** Écriture en cours, annulations / rétablissements de l'historique compris (en attente ou en vol). */
    busy:
      historyBusy ||
      move.isPending ||
      reset.isPending ||
      moveLine.isPending ||
      reanchor.isPending ||
      resetLine.isPending ||
      resetAll.isPending,
  };
}
